/**
 * Template Card (grid view) + EditTemplateForm + InfoRow
 */

import { useState, useRef, memo } from "react";
import {
  ExternalLink,
  Users,
  Hash,
  Calendar,
  Crown,
  ChevronDown,
  ChevronUp,
  Settings,
  Share2,
  Copy,
  Loader2,
  Trash2,
  Check,
  X,
  Star,
  Upload,
  ImageIcon,
  Archive,
  RotateCcw,
  MessageCircle,
  Clock,
  Eye,
  Paintbrush,
  Network,
  ClipboardList,
  Download,
} from "lucide-react";
import type { TemplateMeta, ProjectType } from "../../utils/templateApi";
import { prefetchTemplateContent } from "../../utils/contentPrefetch";
import {
  type UserRecord,
  type UserRole,
  filterUsersByRole,
} from "../../utils/userApi";
import {
  getAccent,
  getInitials,
  getTagColor,
  formatShortDate,
  formatRelativeTime,
  STATUS_CONFIG,
  PROJECT_TYPE_CONFIG,
  type AccentColor,
} from "./dashboardConstants";
import { ActionButton, FormField, TagInput, UserSelectField } from "./DashboardWidgets";

const btnReset =
  "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

/* ================================================================
   Resize image to max 80×80 and return a base64 data URL.
   Keeps aspect ratio. Uses PNG to preserve transparency for logos.
   ================================================================ */

function resizeImageToBase64(
  file: File,
  maxSize = 80,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Failed to load image"));
      img.onload = () => {
        let w = img.width;
        let h = img.height;
        if (w > maxSize || h > maxSize) {
          const ratio = Math.min(maxSize / w, maxSize / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

/* ================================================================
   Template Card
   ================================================================ */

export const TemplateCard = memo(function TemplateCard({
  template,
  isEditing,
  isDeleting,
  actionLoading,
  isAdmin,
  canEditMeta,
  canDelete,
  canDuplicate,
  allTags,
  onOpen,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onDuplicate,
  onDelete,
  onCancelDelete,
  onShare,
  isFavorite,
  onFavorite,
  commentCount,
  onArchive,
  isArchiveMode,
  onRestore,
  allUsers,
  allFolders,
  onWhiteLabel,
  onExport,
}: {
  template: TemplateMeta;
  isEditing: boolean;
  isDeleting: boolean;
  actionLoading: string | null;
  isAdmin: boolean;
  canEditMeta?: boolean;
  canDelete?: boolean;
  canDuplicate?: boolean;
  allTags: string[];
  onOpen: () => void;
  onEdit: () => void;
  onSaveEdit: (updates: Partial<Omit<TemplateMeta, "id" | "createdAt">>) => void;
  onCancelEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onCancelDelete: () => void;
  onShare: () => void;
  isFavorite?: boolean;
  onFavorite?: () => void;
  commentCount?: number;
  onArchive?: () => void;
  isArchiveMode?: boolean;
  onRestore?: () => void;
  allUsers?: UserRecord[];
  allFolders?: { id: string; name: string; color: string }[];
  onWhiteLabel?: () => void;
  onExport?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const accent = getAccent(template.id);
  const users = allUsers || [];

  const effectiveCanEditMeta = canEditMeta ?? isAdmin;
  const effectiveCanDelete = canDelete ?? isAdmin;
  const effectiveCanDuplicate = canDuplicate ?? isAdmin;

  const resolveDisplayName = (username: string) => {
    if (!username) return "";
    const u = users.find((usr) => usr.username === username);
    return u ? u.displayName : username;
  };

  const teamNames = [
    resolveDisplayName(template.characterizer),
    resolveDisplayName(template.marketer),
    resolveDisplayName(template.designer),
    resolveDisplayName(template.developer),
    resolveDisplayName(template.contentWriter || ""),
    resolveDisplayName(template.teamLead || ""),
  ].filter(Boolean);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group border border-gray-100 dark:border-gray-700 hover:border-gray-200 hover:-translate-y-0.5 flex flex-col h-full">
      {/* Accent bar */}
      <div className={`h-1.5 bg-gradient-to-l ${accent.bg} shrink-0`} />

      {/* Card body */}
      <div
        className="p-5 cursor-pointer flex-1 flex flex-col"
        onClick={isEditing ? undefined : onOpen}
        onMouseEnter={() => { if (!isEditing) prefetchTemplateContent(template.id); }}
      >
        <div className="flex items-start gap-3.5 mb-4">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 shadow-sm overflow-hidden ${template.logoUrl ? "bg-white border border-gray-200" : `bg-gradient-to-br ${accent.bg}`}`}>
            {template.logoUrl ? (
              <img
                src={template.logoUrl}
                alt={template.clientName}
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-white text-sm" style={{ fontWeight: 700 }}>
                {getInitials(template.clientName)}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-[#1a1a2e] dark:text-gray-100 text-base truncate leading-tight mb-0.5" style={{ fontWeight: 700 }}>
              {template.clientName || "ללא שם"}
            </h3>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {template.clientNumber && (
                <div className="flex items-center gap-1">
                  <Hash className="w-3 h-3 text-gray-300" />
                  <span className="text-xs text-gray-400">{template.clientNumber}</span>
                </div>
              )}
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                (template.templateType || "branding") === "store"
                  ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                  : (template.templateType || "branding") === "facelift"
                    ? "bg-zap-pale text-zap-glow border-zap-glow/30"
                    : "bg-zap-pale text-zap border-zap-light/30"
              }`} style={{ fontWeight: 600 }}>
                {(template.templateType || "branding") === "store"
                  ? "אתר סחר"
                  : (template.templateType || "branding") === "facelift"
                    ? "מתיחת פנים"
                    : "אתר תדמית"
                }
              </span>
              {template.masterSourceId && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200 flex items-center gap-0.5" style={{ fontWeight: 600 }}>
                  <Crown className="w-2.5 h-2.5" />
                  ממאסטר
                </span>
              )}
              {template.status && STATUS_CONFIG[template.status] && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-1"
                  style={{
                    fontWeight: 600,
                    backgroundColor: STATUS_CONFIG[template.status].bg,
                    color: STATUS_CONFIG[template.status].text,
                    borderColor: STATUS_CONFIG[template.status].border,
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_CONFIG[template.status].dot }} />
                  {STATUS_CONFIG[template.status].label}
                </span>
              )}
              {template.folderId && allFolders && (() => {
                const folder = allFolders.find((f) => f.id === template.folderId);
                if (!folder) return null;
                return (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-0.5"
                    style={{ fontWeight: 500, color: folder.color, borderColor: folder.color + "40", backgroundColor: folder.color + "10" }}
                  >
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 002-2V8a2 2 0 00-2-2h-7.93a2 2 0 01-1.66-.9l-.82-1.2A2 2 0 007.93 3H4a2 2 0 00-2 2v13c0 1.1.9 2 2 2z"/></svg>
                    {folder.name}
                  </span>
                );
              })()}
              {template.clientBranding?.logoUrl && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-0.5"
                  style={{ fontWeight: 500, color: "#7c3aed", borderColor: "#7c3aed40", backgroundColor: "#7c3aed10" }}
                >
                  <Paintbrush className="w-2.5 h-2.5" />
                  WL
                </span>
              )}
              {template.projectType && PROJECT_TYPE_CONFIG[template.projectType] && (() => {
                const ptCfg = PROJECT_TYPE_CONFIG[template.projectType!];
                return (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-0.5"
                    style={{ fontWeight: 700, backgroundColor: ptCfg.bg, color: ptCfg.text, borderColor: ptCfg.border }}
                  >
                    {ptCfg.label}
                  </span>
                );
              })()}
            </div>
          </div>

          <ExternalLink className="w-4 h-4 text-gray-200 group-hover:text-zap transition-colors shrink-0 mt-1" />
        </div>

        {/* Team */}
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
          <Users className="w-3.5 h-3.5 text-gray-300" />
          <span className="truncate">
            {teamNames.length > 0
              ? teamNames.join(" \u00B7 ")
              : "לא הוגדר צוות"}
          </span>
        </div>

        {/* Current owner */}
        {template.currentOwner && (
          <div className="flex items-center gap-1.5 text-xs text-purple-500 mb-3">
            <ClipboardList className="w-3.5 h-3.5 text-purple-400" />
            <span style={{ fontWeight: 600 }}>אחריות:</span>
            <span className="truncate">{template.currentOwner}</span>
          </div>
        )}

        {/* Tags */}
        {(template.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {(template.tags || []).map((tag) => {
              const tc = getTagColor(tag);
              return (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border"
                  style={{ fontWeight: 500, backgroundColor: tc.bg, color: tc.text, borderColor: tc.border }}
                >
                  <span className="w-1 h-1 rounded-full" style={{ backgroundColor: tc.dot }} />
                  {tag}
                </span>
              );
            })}
          </div>
        )}

        {/* Comment count */}
        {typeof commentCount === "number" && commentCount > 0 && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-50 border border-gray-100 mb-3">
            <MessageCircle className="w-3 h-3 text-gray-400" />
            <span className="text-[10px] text-gray-500" style={{ fontWeight: 600 }}>
              {commentCount}
            </span>
          </div>
        )}

        {/* Dates + last edited by */}
        <div className="flex items-center gap-4 text-[11px] text-gray-300 mt-auto pt-1">
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span>{"נוצר"} {formatShortDate(template.createdAt)}</span>
          </div>
          <span>{"עודכן"} {formatRelativeTime(template.updatedAt)}</span>
          {template.deadline && (() => {
            const now = Date.now();
            const diff = template.deadline - now;
            const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
            const isOverdue = diff < 0;
            const isUrgent = !isOverdue && days <= 3;
            const color = isOverdue ? "text-red-500" : isUrgent ? "text-amber-500" : "text-gray-400";
            const label = isOverdue
              ? `באיחור ${Math.abs(days)} ימים`
              : days === 0
                ? "היום"
                : days === 1
                  ? "מחר"
                  : `${days} ימים`;
            return (
              <span className={`flex items-center gap-1 ${color}`} style={{ fontWeight: 600 }}>
                <Clock className="w-3 h-3" />
                {label}
              </span>
            );
          })()}
          {template.lastEditedBy && (
            <span className="mr-auto text-gray-400" style={{ fontWeight: 500 }}>
              {"ע\u05F4י"} {template.lastEditedBy}
            </span>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && !isEditing && (
        <div className="px-5 pb-4 border-t border-gray-50 pt-4 bg-gray-50/50">
          <div className="grid grid-cols-2 gap-2.5 text-xs">
            <InfoRow label="מאפיינת" value={resolveDisplayName(template.characterizer)} accent={accent} />
            <InfoRow label="מקדמת" value={resolveDisplayName(template.marketer)} accent={accent} />
            <InfoRow label="מעצבת" value={resolveDisplayName(template.designer)} accent={accent} />
            <InfoRow label="מפתח" value={resolveDisplayName(template.developer)} accent={accent} />
            <InfoRow label="כותבת תוכן" value={resolveDisplayName(template.contentWriter || "")} accent={accent} />
            <InfoRow label={"\u05DE\u05EA\u05F4\u05DC"} value={resolveDisplayName(template.teamLead || "")} accent={accent} />
          </div>
        </div>
      )}

      {/* Edit form */}
      {isEditing && (
        <EditTemplateForm
          template={template}
          isLoading={actionLoading === `edit-${template.id}`}
          allTags={allTags}
          allUsers={users}
          allFolders={allFolders || []}
          onSave={onSaveEdit}
          onCancel={onCancelEdit}
        />
      )}

      {/* Actions bar */}
      <div className="border-t border-gray-100 px-3 py-2 flex items-center gap-0.5 bg-gray-50/30 shrink-0 mt-auto">
        <ActionButton
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          title={expanded ? "סגור" : "פרטים"}
          hoverClass="hover:text-zap hover:bg-zap-pale"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </ActionButton>
        {effectiveCanEditMeta && !isArchiveMode && (
          <ActionButton
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            title="ערוך פרטים"
            hoverClass="hover:text-zap hover:bg-zap-pale"
          >
            <Settings className="w-4 h-4" />
          </ActionButton>
        )}
        <ActionButton
          onClick={(e) => { e.stopPropagation(); onShare(); }}
          title="שתף קישור"
          hoverClass="hover:text-emerald-600 hover:bg-emerald-50"
        >
          <Share2 className="w-4 h-4" />
        </ActionButton>
        {onWhiteLabel && effectiveCanEditMeta && !isArchiveMode && (
          <ActionButton
            onClick={(e) => { e.stopPropagation(); onWhiteLabel(); }}
            title="מיתוג לקוח"
            hoverClass="hover:text-violet-600 hover:bg-violet-50"
          >
            <Paintbrush className="w-4 h-4" />
          </ActionButton>
        )}
        {!isArchiveMode && (
          <ActionButton
            onClick={(e) => {
              e.stopPropagation();
              window.location.hash = `#/client-board/${template.id}`;
            }}
            title="לוח לקוח"
            hoverClass="hover:text-blue-600 hover:bg-blue-50"
          >
            <Eye className="w-4 h-4" />
          </ActionButton>
        )}
        {effectiveCanDuplicate && !isArchiveMode && (
          <ActionButton
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            disabled={actionLoading === `dup-${template.id}`}
            title="שכפל תבנית"
            hoverClass="hover:text-amber-600 hover:bg-amber-50"
          >
            {actionLoading === `dup-${template.id}` ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </ActionButton>
        )}
        {onExport && !isArchiveMode && (
          <ActionButton
            onClick={(e) => { e.stopPropagation(); onExport(); }}
            disabled={actionLoading === `export-${template.id}`}
            title="ייצא תבנית"
            hoverClass="hover:text-green-500 hover:bg-green-50"
          >
            {actionLoading === `export-${template.id}` ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </ActionButton>
        )}

        <div className="flex-1" />

        {effectiveCanEditMeta && isArchiveMode && onRestore && (
          <ActionButton
            onClick={(e) => { e.stopPropagation(); onRestore(); }}
            title="שחזר מארכיון"
            hoverClass="hover:text-emerald-600 hover:bg-emerald-50"
          >
            <RotateCcw className="w-4 h-4" />
          </ActionButton>
        )}

        {effectiveCanEditMeta && !isArchiveMode && onArchive && (
          <ActionButton
            onClick={(e) => { e.stopPropagation(); onArchive(); }}
            title="העבר לארכיון"
            hoverClass="hover:text-orange-500 hover:bg-orange-50"
          >
            <Archive className="w-4 h-4" />
          </ActionButton>
        )}

        {effectiveCanDelete && (
          isDeleting ? (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-red-500 ml-1" style={{ fontWeight: 500 }}>בטוח?</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onDelete(); } }}
                className="px-2.5 py-1 text-xs bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors cursor-pointer"
                style={{ fontWeight: 600 }}
              >
                {actionLoading === `del-${template.id}` ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  isArchiveMode ? "מחק לצמיתות" : "מחק"
                )}
              </span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onCancelDelete(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onCancelDelete(); } }}
                className="px-2.5 py-1 text-xs bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
                style={{ fontWeight: 500 }}
              >
                ביטול
              </span>
            </div>
          ) : isArchiveMode ? (
            <ActionButton
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              title="מחק לצמיתות"
              hoverClass="hover:text-red-500 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </ActionButton>
          ) : null
        )}
        {onFavorite && !isArchiveMode && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onFavorite(); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onFavorite(); } }}
            className={`p-2 rounded-lg transition-all cursor-pointer ${
              isFavorite
                ? "text-amber-400 hover:text-amber-500 hover:bg-amber-50"
                : "text-gray-400 hover:text-amber-400 hover:bg-amber-50"
            }`}
            title={isFavorite ? "הסר ממועדפים" : "הוסף למועדפים"}
          >
            <Star className="w-4 h-4" fill={isFavorite ? "currentColor" : "none"} />
          </span>
        )}
      </div>
    </div>
  );
});

/* ================================================================
   Info Row
   ================================================================ */

function InfoRow({ label, value, accent }: { label: string; value: string; accent: AccentColor }) {
  return (
    <div className={`px-2.5 py-1.5 rounded-lg ${accent.light} ${accent.border} border`}>
      <span className="text-gray-400 text-[10px] block mb-0.5">{label}</span>
      <span className={`${accent.text} text-xs`} style={{ fontWeight: 600 }}>
        {value || "\u2014"}
      </span>
    </div>
  );
}

/* ================================================================
   Edit Template Form (inline) with user assignment dropdowns
   ================================================================ */

function EditTemplateForm({
  template,
  isLoading,
  allTags,
  allUsers,
  allFolders,
  onSave,
  onCancel,
}: {
  template: TemplateMeta;
  isLoading: boolean;
  allTags: string[];
  allUsers: UserRecord[];
  allFolders: { id: string; name: string; color: string }[];
  onSave: (updates: Partial<Omit<TemplateMeta, "id" | "createdAt">>) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    clientName: template.clientName,
    clientNumber: template.clientNumber,
    characterizer: template.characterizer,
    marketer: template.marketer,
    designer: template.designer,
    developer: template.developer,
    contentWriter: template.contentWriter || "",
    teamLead: template.teamLead || "",
    status: template.status || "",
    currentOwner: template.currentOwner || "",
    folderId: template.folderId || "",
    projectType: (template.projectType || "") as "" | ProjectType,
  });
  const [editTags, setEditTags] = useState<string[]>(template.tags || []);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(template.logoUrl);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [deadlineStr, setDeadlineStr] = useState<string>(() => {
    if (!template.deadline) return "";
    const d = new Date(template.deadline);
    return d.toISOString().slice(0, 10);
  });
  const tagPendingRef = useRef("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const usersByRole = (role: UserRole) => filterUsersByRole(allUsers, role);

  const getFinalTags = (): string[] => {
    let tags = editTags;
    const pending = tagPendingRef.current.trim();
    if (pending && !tags.includes(pending)) {
      tags = [...tags, pending];
      setEditTags(tags);
    }
    return tags;
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError(null);

    if (!file.type.startsWith("image/")) {
      setLogoError("קובץ לא תקין — יש להעלות תמונה בלבד");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setLogoError("קובץ גדול מדי — מקסימום 2MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setLogoUploading(true);
    try {
      const dataUrl = await resizeImageToBase64(file, 80);
      setLogoUrl(dataUrl);
    } catch (err) {
      console.error("Logo upload error:", err);
      setLogoError("שגיאה בעיבוד התמונה");
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeLogo = () => {
    setLogoUrl(undefined);
    setLogoError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="px-5 pb-4 border-t border-zap-pale pt-4 bg-zap-pale/30 space-y-3">
      {/* Logo upload section */}
      <div>
        <label className="block text-xs text-gray-500 mb-1.5" style={{ fontWeight: 600 }}>
          לוגו
        </label>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-white shrink-0">
            {logoUploading ? (
              <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
            ) : logoUrl ? (
              <img src={logoUrl} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <ImageIcon className="w-5 h-5 text-gray-300" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={logoUploading}
              className={`${btnReset} flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-gray-200 bg-white hover:bg-gray-50 text-gray-600`}
              style={{ fontWeight: 500 }}
            >
              <Upload className="w-3.5 h-3.5" />
              {logoUrl ? "החלף" : "העלאת לוגו"}
            </button>
            {logoUrl && (
              <button
                type="button"
                onClick={removeLogo}
                className={`${btnReset} flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50`}
                style={{ fontWeight: 500 }}
              >
                <X className="w-3 h-3" />
                הסר
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            className="hidden"
          />
        </div>
        {logoError && (
          <div className="text-xs text-red-500 mt-1.5">{logoError}</div>
        )}
      </div>

      <FormField
        label="שם לקוח"
        value={form.clientName}
        onChange={(v) => setForm((p) => ({ ...p, clientName: v }))}
      />
      <FormField
        label="מספר לקוח"
        value={form.clientNumber}
        onChange={(v) => setForm((p) => ({ ...p, clientNumber: v }))}
      />

      {/* Team assignment */}
      <div>
        <label className="block text-xs text-gray-500 mb-2" style={{ fontWeight: 600 }}>
          הקצאת צוות
        </label>
        <div className="grid grid-cols-2 gap-3">
          <UserSelectField
            label="מאפיינת"
            value={form.characterizer}
            onChange={(v) => setForm((p) => ({ ...p, characterizer: v }))}
            users={usersByRole("characterizer")}
            placeholder="בחר מאפיינת..."
          />
          <UserSelectField
            label="מקדמת"
            value={form.marketer}
            onChange={(v) => setForm((p) => ({ ...p, marketer: v }))}
            users={usersByRole("marketer")}
            placeholder="בחר מקדמת..."
          />
          <UserSelectField
            label="מעצבת"
            value={form.designer}
            onChange={(v) => setForm((p) => ({ ...p, designer: v }))}
            users={usersByRole("designer")}
            placeholder="בחר מעצבת..."
          />
          <UserSelectField
            label="מפתח"
            value={form.developer}
            onChange={(v) => setForm((p) => ({ ...p, developer: v }))}
            users={usersByRole("developer")}
            placeholder="בחר מפתח..."
          />
          <UserSelectField
            label="כותבת תוכן"
            value={form.contentWriter}
            onChange={(v) => setForm((p) => ({ ...p, contentWriter: v }))}
            users={usersByRole("content_writer")}
            placeholder="בחר כותבת תוכן..."
          />
          <UserSelectField
            label={"\u05DE\u05EA\u05F4\u05DC"}
            value={form.teamLead}
            onChange={(v) => setForm((p) => ({ ...p, teamLead: v }))}
            users={usersByRole("team_lead")}
            placeholder={"\u05D1\u05D7\u05E8 \u05DE\u05EA\u05F4\u05DC..."}
          />
        </div>
      </div>

      <TagInput tags={editTags} onChange={setEditTags} allTags={allTags} pendingRef={tagPendingRef} />

      {/* Workflow status selector */}
      <div>
        <label className="block text-xs text-gray-500 mb-1.5" style={{ fontWeight: 600 }}>
          שלב בפלואו
        </label>
        <select
          value={form.status || ""}
          onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as any }))}
          className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-purple-300"
          style={{ fontWeight: 600 }}
          dir="rtl"
        >
          <option value="">ללא</option>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        {form.status && STATUS_CONFIG[form.status] && (
          <div className="mt-1.5 flex items-center gap-1.5">
            <span
              className="text-[10px] px-2 py-0.5 rounded border flex items-center gap-1 w-fit"
              style={{
                fontWeight: 600,
                backgroundColor: STATUS_CONFIG[form.status].bg,
                color: STATUS_CONFIG[form.status].text,
                borderColor: STATUS_CONFIG[form.status].border,
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_CONFIG[form.status].dot }} />
              {STATUS_CONFIG[form.status].label}
            </span>
          </div>
        )}
      </div>

      {/* Current owner / responsibility */}
      <FormField
        label="אחריות נוכחית"
        value={form.currentOwner || ""}
        onChange={(v) => setForm((p) => ({ ...p, currentOwner: v }))}
        placeholder="שם האחראי/ת הנוכחי/ת"
      />

      {/* Project type selector (SEO / WP / WIX) */}
      <div>
        <label className="block text-xs text-gray-500 mb-1.5" style={{ fontWeight: 600 }}>
          סוג פרויקט
        </label>
        <div className="flex items-center gap-1 flex-wrap">
          {([{ value: "" as "" | ProjectType, label: "ללא" }, { value: "seo" as ProjectType, label: "SEO" }, { value: "wp" as ProjectType, label: "WP" }, { value: "wix" as ProjectType, label: "WIX" }]).map((opt) => {
            const isActive = form.projectType === opt.value;
            const cfg = opt.value ? PROJECT_TYPE_CONFIG[opt.value] : { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" };
            return (
              <button
                key={opt.value || "__none"}
                type="button"
                onClick={() => setForm((p) => ({ ...p, projectType: opt.value }))}
                className={`${btnReset} flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border`}
                style={{
                  fontWeight: isActive ? 700 : 500,
                  backgroundColor: isActive ? cfg.bg : "#fff",
                  color: isActive ? cfg.text : "#9ca3af",
                  borderColor: isActive ? cfg.border : "#e5e7eb",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Deadline date picker */}
      <div>
        <label className="block text-xs text-gray-500 mb-1.5" style={{ fontWeight: 600 }}>
          דדליין
        </label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={deadlineStr}
            onChange={(e) => setDeadlineStr(e.target.value)}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            style={{ direction: "ltr" }}
          />
          {deadlineStr && (
            <button
              type="button"
              className={`${btnReset} text-xs text-red-400 hover:text-red-600 px-2`}
              onClick={() => setDeadlineStr("")}
            >
              נקה
            </button>
          )}
        </div>
      </div>

      {/* Folder selector */}
      {allFolders.length > 0 && (
        <div>
          <label className="block text-xs text-gray-500 mb-1.5" style={{ fontWeight: 600 }}>
            תיקייה
          </label>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setForm((p) => ({ ...p, folderId: "" }))}
              className={`${btnReset} flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border`}
              style={{
                fontWeight: !form.folderId ? 700 : 500,
                backgroundColor: !form.folderId ? "#f3f4f6" : "#fff",
                color: !form.folderId ? "#374151" : "#9ca3af",
                borderColor: !form.folderId ? "#9ca3af" : "#e5e7eb",
              }}
            >
              ללא
            </button>
            {allFolders.map((f) => {
              const isActive = form.folderId === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, folderId: f.id }))}
                  className={`${btnReset} flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border`}
                  style={{
                    fontWeight: isActive ? 700 : 500,
                    backgroundColor: isActive ? f.color + "15" : "#fff",
                    color: isActive ? f.color : "#9ca3af",
                    borderColor: isActive ? f.color + "60" : "#e5e7eb",
                  }}
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 002-2V8a2 2 0 00-2-2h-7.93a2 2 0 01-1.66-.9l-.82-1.2A2 2 0 007.93 3H4a2 2 0 00-2 2v13c0 1.1.9 2 2 2z"/></svg>
                  {f.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <span
          role="button"
          tabIndex={0}
          onClick={isLoading ? undefined : () => {
            const ft = getFinalTags();
            const deadline = deadlineStr ? new Date(deadlineStr + "T23:59:59").getTime() : undefined;
            const { projectType, ...rest } = form;
            onSave({ ...rest, projectType: projectType || undefined, tags: ft.length > 0 ? ft : undefined, logoUrl, deadline });
          }}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !isLoading) {
              const ft = getFinalTags();
              const deadline = deadlineStr ? new Date(deadlineStr + "T23:59:59").getTime() : undefined;
              const { projectType, ...rest } = form;
              onSave({ ...rest, projectType: projectType || undefined, tags: ft.length > 0 ? ft : undefined, logoUrl, deadline });
            }
          }}
          className="flex-1 flex items-center justify-center gap-1 px-4 py-2 bg-zap text-white rounded-xl text-sm hover:bg-zap-navy transition-all cursor-pointer disabled:opacity-50 shadow-sm"
          style={{ fontWeight: 600 }}
        >
          {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          שמור
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={onCancel}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onCancel(); }}
          className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm hover:bg-gray-200 transition-colors cursor-pointer"
          style={{ fontWeight: 500 }}
        >
          ביטול
        </span>
      </div>
    </div>
  );
}